import React, { useEffect, useMemo, useState } from 'react';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CloseIcon from '@mui/icons-material/Close';
import HubIcon from '@mui/icons-material/Hub';
import InsightsIcon from '@mui/icons-material/Insights';
import MenuIcon from '@mui/icons-material/Menu';
import TimelineIcon from '@mui/icons-material/Timeline';
import { gql, useApolloClient } from '@apollo/client';
import { useNavigate } from 'react-router-dom';

import { getReadGraphPath } from '../graphRoutes';
import './GraphLanding.css';

const CONTACT_EMAIL = 'contact@howyoumet.us';

const GRAPH_EXISTS_QUERY = gql`
  query LandingGraphExists($graphName: String) {
    graph(graphName: $graphName) {
      _id
    }
  }
`;

const getDemoMailtoHref = (isWeddingVariant) => {
  const subject = encodeURIComponent(isWeddingVariant ? 'How You Met Us wedding demo' : 'How You Met Me demo');
  const body = encodeURIComponent('Hi Kevin,\n\nI would love to see a demo.\n\nMap type:\nEvent or group:\nTiming:\n');
  return `mailto:${CONTACT_EMAIL}?subject=${subject}&body=${body}`;
};

const unsplashImage = (id, width = 1800, quality = 90) => (
  `https://images.unsplash.com/${id}?auto=format&fit=crop&w=${width}&q=${quality}&dpr=2`
);

const graphNodes = [
  { x: 18, y: 30, size: 18, color: '#00d5ff', name: 'Maya', photo: unsplashImage('photo-1494790108377-be9c29b29330', 900) },
  { x: 34, y: 18, size: 24, color: '#ff2d95', name: 'Alex', photo: unsplashImage('photo-1507003211169-0a1dd7228f2d', 900) },
  { x: 52, y: 34, size: 16, color: '#ffd60a', name: 'Nina', photo: unsplashImage('photo-1438761681033-6461ffad8d80', 900) },
  { x: 70, y: 20, size: 22, color: '#32ff6a', name: 'Jordan', photo: unsplashImage('photo-1500648767791-00dcc994a43e', 900) },
  { x: 82, y: 42, size: 17, color: '#8b5cf6', name: 'Sam', photo: unsplashImage('photo-1534528741775-53994a69daeb', 900) },
  { x: 28, y: 58, size: 20, color: '#ff8a00', name: 'Priya', photo: unsplashImage('photo-1531123897727-8f129e1688ce', 900) },
  { x: 48, y: 70, size: 26, color: '#00d5ff', name: 'Kevin', photo: unsplashImage('photo-1506794778202-cad84cf45f1d', 900) },
  { x: 68, y: 62, size: 18, color: '#ff2d95', name: 'Avery', photo: unsplashImage('photo-1544005313-94ddf0286df2', 900) },
  { x: 84, y: 76, size: 22, color: '#ffd60a', name: 'Marcus', photo: unsplashImage('photo-1508214751196-bcfd4ca60f91', 900) },
];

const weddingPeople = {
  bride: {
    label: 'Bride',
    photo: unsplashImage('photo-1520854221256-17451cc331bf', 1800),
  },
  groom: {
    label: 'Groom',
    photo: unsplashImage('photo-1511285560929-80b456fea0bc', 1800),
  },
  couple: unsplashImage('photo-1519741497674-611481863552', 2200),
  weddingParty: [
    unsplashImage('photo-1523438885200-e635ba2c371e', 1800),
    unsplashImage('photo-1519225421980-715cb0215aed', 2200),
    unsplashImage('photo-1509610973147-232dfea52a97', 1800),
    unsplashImage('photo-1505932794465-147d1f1b2c97', 1800),
  ],
  guests: [
    unsplashImage('photo-1529634597503-139d3726fed5', 1600),
    unsplashImage('photo-1511285560929-80b456fea0bc', 1600),
    unsplashImage('photo-1496747611176-843222e1e57c', 1600),
    unsplashImage('photo-1523438885200-e635ba2c371e', 1600),
    unsplashImage('photo-1519741497674-611481863552', 1600),
    unsplashImage('photo-1519225421980-715cb0215aed', 1600),
  ],
};

const productPhotos = [
  unsplashImage('photo-1517048676732-d65bc937f952', 2200),
  unsplashImage('photo-1529156069898-49953e39b3ac', 2200),
  unsplashImage('photo-1521737711867-e3b97375f902', 2200),
  unsplashImage('photo-1529333166437-7750a6dd5a70', 2200),
  unsplashImage('photo-1511632765486-a01980e01a18', 2200),
  unsplashImage('photo-1522202176988-66273c2fd55f', 2200),
];

const graphEdges = [
  [0, 1],
  [1, 2],
  [2, 3],
  [3, 4],
  [0, 5],
  [5, 6],
  [6, 7],
  [7, 8],
  [2, 6],
  [3, 7],
];

const featureCards = [
  {
    icon: HubIcon,
    title: 'Relationship Maps',
    body: 'See the original introductions, small-world connections, and the people who quietly hold a group together.',
  },
  {
    icon: TimelineIcon,
    title: 'Formation Timeline',
    body: 'Replay how a community formed over time, one connection at a time, without moving the layout around.',
  },
  {
    icon: InsightsIcon,
    title: 'Group Insights',
    body: 'Explore degrees of separation, central connectors, oldest bonds, and what-if scenarios for the network.',
  },
];

const graphTypeCards = [
  {
    title: 'Social Map',
    body: 'Friend groups, how-we-met stories, degrees of separation, hidden connectors, and relationship history playback.',
  },
  {
    title: 'Family Tree',
    body: 'Birth and death dates, children, siblings, partners, marriages, oldest ancestor, longest life, and family-specific superlatives.',
  },
  {
    title: 'Wedding',
    body: 'Couple, family, wedding party, and guest relationships with celebration-specific insights around who connects the room.',
  },
];

const weddingTypeCards = [
  {
    title: 'Guest Map',
    body: 'Show the couple, families, wedding party, and guests in one private relationship map.',
  },
  {
    title: 'Connection Paths',
    body: 'Let guests see how they are connected to someone else through the people already in the map.',
  },
  {
    title: 'Memory Map',
    body: 'Collect stories, dates, photos, and new connections in a private keepsake guests can revisit.',
  },
];

const makeWeddingFeatureCards = ([first, second, third]) => [
  { icon: HubIcon, ...first },
  { icon: TimelineIcon, ...second },
  { icon: InsightsIcon, ...third },
];

const weddingFeatureSets = {
  concierge: makeWeddingFeatureCards([
    {
      title: 'Guest Connection Paths',
      body: 'Guests can pick two people and see the shortest relationship path between them.',
    },
    {
      title: 'Mobile Guest Cards',
      body: 'Person and connection drawers are built for phones, with photos, stories, and relationship context.',
    },
    {
      title: 'Connector Highlights',
      body: 'Insight cards identify central people, bridge connections, and the paths that hold the group together.',
    },
  ]),
  seating: makeWeddingFeatureCards([
    {
      title: 'Guest Group Context',
      body: 'Use relationship categories, stories, and photos to understand how guests fit into the celebration.',
    },
    {
      title: 'Reception Stories',
      body: 'Connection cards keep the date, category, and story of how two people met in one place.',
    },
    {
      title: 'Shared Guest Context',
      body: 'People, stories, and photos stay organized around the relationships guests care about.',
    },
  ]),
  weekend: makeWeddingFeatureCards([
    {
      title: 'Weekend-Wide Map',
      body: 'Use one private map for the people and stories around the whole wedding weekend.',
    },
    {
      title: 'Known Faces First',
      body: 'Guests can browse photos and names before they arrive, then open connection details when they need context.',
    },
    {
      title: 'Familiar Faces Fast',
      body: 'The path finder shows who two people have in common, including small-world connections.',
    },
  ]),
  families: makeWeddingFeatureCards([
    {
      title: 'In-Law Orientation',
      body: 'Make the combined family easier to browse with people, partners, siblings, children, and relationship details.',
    },
    {
      title: 'Family Story Paths',
      body: 'Show how family guests are connected to the couple without making anyone memorize a tree.',
    },
    {
      title: 'Blended Family Support',
      body: 'Give modern families a polished way to represent real relationships with care.',
    },
  ]),
  party: makeWeddingFeatureCards([
    {
      title: 'Wedding Party Context',
      body: 'Give the wedding party a fast way to look up guests, stories, and relationship paths.',
    },
    {
      title: 'Guest Lookup',
      body: 'Search the map, open person cards, and see how someone is connected to the couple.',
    },
    {
      title: 'Group Overlaps',
      body: 'Use categories and small-world links to see where friend groups, families, and coworkers overlap.',
    },
  ]),
  stories: makeWeddingFeatureCards([
    {
      title: 'Guest Stories',
      body: 'Guests can share memories and photos that become part of the wedding keepsake.',
    },
    {
      title: 'Tagged People',
      body: 'Stories can tag who was there, turning anecdotes into discoverable relationship history.',
    },
    {
      title: 'Living Guest Book',
      body: 'More personal than signatures: the keepsake grows with real stories from the weekend.',
    },
  ]),
  toasts: makeWeddingFeatureCards([
    {
      title: 'Story Research',
      body: 'Browse origin stories, dates, photos, and people tags when you want more personal context.',
    },
    {
      title: 'Relationship History',
      body: 'Use the timeline to watch how relationships formed without moving the saved map layout.',
    },
    {
      title: 'Couple Story Context',
      body: 'Show how the people in the room helped shape the couple’s shared life.',
    },
  ]),
  photo: makeWeddingFeatureCards([
    {
      title: 'Photo-First Profiles',
      body: 'People cards and map nodes use photos so guests can recognize the people they are reading about.',
    },
    {
      title: 'Photo Suggestions',
      body: 'Guests can help add profile photos from the read-only map so faces stay familiar.',
    },
    {
      title: 'Story Photos',
      body: 'Stories can include people tags, dates, and photos so memories stay connected to the right people.',
    },
  ]),
  memory: makeWeddingFeatureCards([
    {
      title: 'Post-Wedding Keepsake',
      body: 'The map stays useful after the day, with stories and photos tied to real people.',
    },
    {
      title: 'New Connections',
      body: 'Guests can add how they met during the weekend so the keepsake keeps growing.',
    },
    {
      title: 'Private Archive',
      body: 'A more intimate memory layer than a public album or hashtag.',
    },
  ]),
  premium: makeWeddingFeatureCards([
    {
      title: 'Wedding Website Add-On',
      body: 'A private map link couples can share alongside the wedding details guests already have.',
    },
    {
      title: 'Private By URL Name',
      body: 'Guests need the map name or link; there is no public map directory on the landing page.',
    },
    {
      title: 'Editor Workspace',
      body: 'Couples can keep the map polished from edit mode as stories and photos come in.',
    },
  ]),
};

const landingVariants = {
  1: {
    key: 'command',
    eyebrow: 'Private relationship maps',
    title: 'Find the story behind every connection.',
    body: 'Open a shared map by name to explore social maps, family trees, and wedding networks with views built for each story.',
    label: 'Map name or URL name',
    placeholder: 'Enter map name or URL name',
    button: 'Open',
    panelA: ['Shortest path', '4 degrees'],
    panelB: ['Most central', 'Connector found'],
    featureIntro: 'Map styles',
    featureTitle: 'Different relationships need different tools.',
    metrics: ['Private by URL name', 'Timeline playback', 'Photo-first map'],
  },
  2: {
    key: 'atlas',
    eyebrow: 'A living map of your people',
    title: 'Turn a group into something you can actually explore.',
    body: 'Search a map name and step into the introductions, shortcuts, and stories that made the room feel connected.',
    label: 'Which map are we opening?',
    placeholder: 'try: wedding-weekend',
    button: 'Explore',
    panelA: ['Hidden bridge', '2 circles linked'],
    panelB: ['Origin story', 'First bond found'],
    featureIntro: 'Built like an atlas',
    featureTitle: 'Every node, route, and story stays easy to follow.',
    metrics: ['Small-world links', 'Story collection', 'Mobile-ready drawers'],
  },
  3: {
    key: 'wedding',
    eyebrow: 'Wedding guest intelligence',
    title: 'Help every guest understand how the room fits together.',
    body: 'Open a wedding map to see the couple, families, wedding party, and the relationships that make the celebration feel personal.',
    label: 'Wedding map name',
    placeholder: 'enter the couple or URL name',
    button: 'View',
    panelA: ['Table magic', 'Guests connected'],
    panelB: ['Couple path', 'Center of the room'],
    featureIntro: 'Wedding mode',
    featureTitle: 'A polished way to explain who is who before the toast.',
    metrics: ['Families linked', 'Guest stories', 'Room connectors'],
  },
  4: {
    key: 'family',
    eyebrow: 'Family tree with context',
    title: 'Give the family tree its missing stories.',
    body: 'Family maps support partners, siblings, children, dates, and the small memories that make a lineage more than a chart.',
    label: 'Family map name',
    placeholder: 'enter family URL name',
    button: 'Open tree',
    panelA: ['Generations', 'Stories attached'],
    panelB: ['Branches', 'No context lost'],
    featureIntro: 'Family mode',
    featureTitle: 'People, dates, partners, and memories in one interactive view.',
    metrics: ['Birth dates', 'Partners', 'Children'],
  },
  5: {
    key: 'timeline',
    eyebrow: 'Watch the group form',
    title: 'Replay the exact moment every connection appeared.',
    body: 'The timeline keeps people in place while relationships animate in, so you can understand how the network came together over time.',
    label: 'Timeline map name',
    placeholder: 'enter the map to replay',
    button: 'Replay',
    panelA: ['History mode', 'Edges appear in order'],
    panelB: ['Disjoint trees', 'Tracked each step'],
    featureIntro: 'Relationship history',
    featureTitle: 'A map can feel like a story, not a static diagram.',
    metrics: ['Step controls', 'Keyboard shortcuts', 'Stable layout'],
  },
  6: {
    key: 'insights',
    eyebrow: 'Network insights',
    title: 'Ask better questions about the people you know.',
    body: 'Find shortest paths, influential connectors, oldest bonds, quiet bridges, and what changes when one person disappears.',
    label: 'Insight map name',
    placeholder: 'enter a map to analyze',
    button: 'Analyze',
    panelA: ['What if?', 'Impact simulated'],
    panelB: ['Best of map', 'Awards ready'],
    featureIntro: 'Discovery tools',
    featureTitle: 'Insight cards that animate answers directly on the map.',
    metrics: ['Shortest path', 'Central people', 'Breakup impact'],
  },
  7: {
    key: 'minimal',
    eyebrow: 'Simple private map access',
    title: 'One name. One map. Everyone connected.',
    body: 'No public directory, no noisy navigation. People open the exact map they already know by its private URL name.',
    label: 'Private map name',
    placeholder: 'type the URL name',
    button: 'Go',
    panelA: ['Access', 'Name required'],
    panelB: ['Clean view', 'Map only'],
    featureIntro: 'Private by design',
    featureTitle: 'The landing page stays calm; the map does the talking.',
    metrics: ['No directory', 'Share link', 'Read-only mode'],
  },
  8: {
    key: 'studio',
    eyebrow: 'Curated social storytelling',
    title: 'Make the invisible social architecture visible.',
    body: 'How You Met Me turns introductions, memories, and photos into a premium interactive artifact for a real community.',
    label: 'Memory map name',
    placeholder: 'enter the story URL',
    button: 'Begin',
    panelA: ['Stories', 'Tagged to people'],
    panelB: ['Photos', 'Faces stay crisp'],
    featureIntro: 'Story-first maps',
    featureTitle: 'Built for groups that have history worth remembering.',
    metrics: ['Tagged stories', 'Dates', 'Photo memories'],
  },
  9: {
    key: 'editorial',
    eyebrow: 'For reunions, weddings, teams, and families',
    title: 'A beautiful way to answer, “wait, how do you know them?”',
    body: 'Enter the map name and get a full-screen relationship map with photo cards, connection details, and visual insights.',
    label: 'Known map name',
    placeholder: 'enter the name someone shared',
    button: 'Find it',
    panelA: ['Question solved', 'Path revealed'],
    panelB: ['Small world', 'Extra links labeled'],
    featureIntro: 'Made for real gatherings',
    featureTitle: 'When the group is complicated, the map makes it feel obvious.',
    metrics: ['Reunions', 'Teams', 'Events'],
  },
  10: {
    key: 'launch',
    eyebrow: 'How You Met Me',
    title: 'A private home for every group origin story.',
    body: 'Open a map by its URL name, then browse people, stories, timelines, suggestions, and insight animations from any screen size.',
    label: 'Map URL name',
    placeholder: 'enter the map URL name',
    button: 'Launch',
    panelA: ['Suggestions', 'Guest contributions'],
    panelB: ['Map types', 'Social, family, wedding'],
    featureIntro: 'Complete map platform',
    featureTitle: 'A serious relationship map, wrapped in a simple invitation.',
    metrics: ['Social map', 'Family tree', 'Wedding map'],
  },
  11: {
    key: 'wedding-concierge',
    asset: 'concierge',
    eyebrow: 'Wedding guest concierge',
    title: 'Give guests a clearer way into the room.',
    body: 'A private wedding map helps guests see the couple, the wedding party, family sides, and the relationship paths that connect people.',
    label: 'Wedding map name',
    placeholder: 'enter your wedding URL name',
    button: 'Open wedding',
    panelA: ['Path finder', 'Ready before cocktail hour'],
    panelB: ['Guest cards', 'Photos and stories'],
    featureIntro: 'Wedding-day sales feature',
    featureTitle: 'A relationship map guests can browse before and during the wedding.',
    metrics: ['Private map', 'Guest paths', 'Phone friendly'],
    featureCards: weddingFeatureSets.concierge,
    typeCards: weddingTypeCards,
  },
  12: {
    key: 'wedding-seating',
    asset: 'seating',
    eyebrow: 'Seating chart intelligence',
    title: 'Make guest connections easier to understand.',
    body: 'Use the wedding map to show guests, relationship categories, shared stories, and the people who connect different parts of the room.',
    label: 'Seating map name',
    placeholder: 'enter the reception URL name',
    button: 'View map',
    panelA: ['Guest groups', 'Shared threads'],
    panelB: ['Connector', 'Found in map'],
    featureIntro: 'Reception upgrade',
    featureTitle: 'A guest-friendly layer for the relationships around the reception.',
    metrics: ['Guest context', 'Shared stories', 'Photo memories'],
    featureCards: weddingFeatureSets.seating,
    typeCards: weddingTypeCards,
  },
  13: {
    key: 'wedding-weekend',
    asset: 'weekend',
    eyebrow: 'Welcome weekend map',
    title: 'Make the whole weekend feel hosted.',
    body: 'A wedding map gives guests one private place to browse the people, relationships, and stories around the celebration.',
    label: 'Weekend map name',
    placeholder: 'enter the weekend URL name',
    button: 'Start weekend',
    panelA: ['People', 'Photos and names'],
    panelB: ['Groups', 'Families and friends'],
    featureIntro: 'Multi-event weddings',
    featureTitle: 'A social guide guests can open whenever they need context.',
    metrics: ['Private link', 'Guest stories', 'Path finder'],
    featureCards: weddingFeatureSets.weekend,
    typeCards: weddingTypeCards,
  },
  14: {
    key: 'wedding-families',
    asset: 'families',
    eyebrow: 'Two-family merge',
    title: 'Help both families learn the room.',
    body: 'Map parents, siblings, cousins, partners, and family friends so guests understand the new combined family without needing a whispered explainer.',
    label: 'Family wedding map',
    placeholder: 'enter the family URL name',
    button: 'Meet families',
    panelA: ['Family sides', 'Beautifully merged'],
    panelB: ['New links', 'Cousins now connected'],
    featureIntro: 'Family-first weddings',
    featureTitle: 'The family tree meets the reception floor.',
    metrics: ['Parents', 'Siblings', 'New in-laws'],
    featureCards: weddingFeatureSets.families,
    typeCards: weddingTypeCards,
  },
  15: {
    key: 'wedding-party',
    asset: 'party',
    eyebrow: 'Wedding party host mode',
    title: 'Give the wedding party better context.',
    body: 'Bridesmaids, groomsmen, and close friends can use the map to look up guests, open stories, and understand how people are connected.',
    label: 'Wedding party map',
    placeholder: 'enter the party URL name',
    button: 'Open map',
    panelA: ['Guest lookup', 'Names and photos'],
    panelB: ['Paths', 'Connections shown'],
    featureIntro: 'Hospitality layer',
    featureTitle: 'Make your closest people better informed about the room.',
    metrics: ['Guest lookup', 'Story cards', 'Path finder'],
    featureCards: weddingFeatureSets.party,
    typeCards: weddingTypeCards,
  },
  16: {
    key: 'wedding-stories',
    asset: 'stories',
    eyebrow: 'Story collection',
    title: 'Capture the stories people tell all night.',
    body: 'Guests can add memories, tag who was there, and send photos so the wedding map becomes a living guest book.',
    label: 'Memory map name',
    placeholder: 'enter the memory URL name',
    button: 'Collect stories',
    panelA: ['Guest book', 'Stories with faces'],
    panelB: ['Memories', 'Editor polished'],
    featureIntro: 'After-party memory engine',
    featureTitle: 'A wedding keepsake that grows during the night.',
    metrics: ['Story dates', 'Photo memories', 'Guest book'],
    featureCards: weddingFeatureSets.stories,
    typeCards: weddingTypeCards,
  },
  17: {
    key: 'wedding-toasts',
    asset: 'toasts',
    eyebrow: 'Toast and speech prep',
    title: 'Make every speech more personal.',
    body: 'Browse relationship paths, dated stories, oldest bonds, and the people who brought the group together before writing a toast.',
    label: 'Toast map name',
    placeholder: 'enter the toast URL name',
    button: 'Find stories',
    panelA: ['Story search', 'Personal context'],
    panelB: ['Origin bonds', 'Oldest non-family'],
    featureIntro: 'Speaker support',
    featureTitle: 'Better speeches start with better relationship context.',
    metrics: ['Toast ideas', 'Origin stories', 'Connector awards'],
    featureCards: weddingFeatureSets.toasts,
    typeCards: weddingTypeCards,
  },
  18: {
    key: 'wedding-photo',
    asset: 'photo',
    eyebrow: 'Photo moment planner',
    title: 'Keep wedding photos tied to the right people.',
    body: 'Use photo-first people cards and story photos so wedding memories stay connected to names and relationships.',
    label: 'Photo map name',
    placeholder: 'enter the photo URL name',
    button: 'View photos',
    panelA: ['Profile photos', 'Faces stay clear'],
    panelB: ['Story photos', 'People tagged'],
    featureIntro: 'Photo-friendly map',
    featureTitle: 'Photos, people, and stories stay connected in one private map.',
    metrics: ['Profile photos', 'Story photos', 'People tags'],
    featureCards: weddingFeatureSets.photo,
    typeCards: weddingTypeCards,
  },
  19: {
    key: 'wedding-memory',
    asset: 'memory',
    eyebrow: 'Living wedding keepsake',
    title: 'Let the guest list become the memory book.',
    body: 'After the wedding, the map stays useful: guests revisit stories, add missed connections, upload photos, and remember how everyone fit together.',
    label: 'Memory map name',
    placeholder: 'enter the keepsake URL name',
    button: 'Open keepsake',
    panelA: ['After wedding', 'Still growing'],
    panelB: ['Memories', 'Tied to people'],
    featureIntro: 'Post-wedding value',
    featureTitle: 'A digital keepsake that is more personal than a gallery link.',
    metrics: ['Living guest book', 'Photo memories', 'New connections'],
    featureCards: weddingFeatureSets.memory,
    typeCards: weddingTypeCards,
  },
  20: {
    key: 'wedding-premium',
    asset: 'premium',
    eyebrow: 'Premium wedding add-on',
    title: 'A wedding website feature guests will actually use.',
    body: 'Share a private relationship map alongside the wedding website so guests can browse people, stories, and paths from their phones.',
    label: 'Premium map name',
    placeholder: 'enter the premium URL name',
    button: 'Preview it',
    panelA: ['Private link', 'Name required'],
    panelB: ['Editor tools', 'Polished map'],
    featureIntro: 'For planners and couples',
    featureTitle: 'A polished add-on for modern wedding websites.',
    metrics: ['Private URL', 'Wedding map', 'Story archive'],
    featureCards: weddingFeatureSets.premium,
    typeCards: weddingTypeCards,
  },
};

function PreviewCard({ className, label, value }) {
  return (
    <div className={`graph-landing-preview-card ${className}`}>
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function NetworkAsset({ panelA, panelB }) {
  return (
    <div className="graph-landing-preview graph-landing-asset graph-landing-asset--command" aria-hidden="true">
      <svg viewBox="0 0 100 100" role="img">
        <defs>
          {graphNodes.map((node, index) => {
            const radius = node.size / 2;
            return (
              <clipPath key={`clip-${index}`} id={`graph-landing-photo-${index}`}>
                <circle cx={node.x} cy={node.y} r={radius - 1.8} />
              </clipPath>
            );
          })}
        </defs>
        {graphEdges.map(([from, to]) => {
          const start = graphNodes[from];
          const end = graphNodes[to];
          return (
            <line
              key={`${from}-${to}`}
              x1={start.x}
              y1={start.y}
              x2={end.x}
              y2={end.y}
            />
          );
        })}
        {graphNodes.map((node, index) => (
          <g key={index} className={`graph-landing-node graph-landing-node-${index}`}>
            <circle
              className="graph-landing-node-glow"
              cx={node.x}
              cy={node.y}
              r={node.size / 2}
              fill={node.color}
            />
            <image
              href={node.photo}
              x={node.x - (node.size / 2) + 1.8}
              y={node.y - (node.size / 2) + 1.8}
              width={node.size - 3.6}
              height={node.size - 3.6}
              preserveAspectRatio="xMidYMid slice"
              clipPath={`url(#graph-landing-photo-${index})`}
            />
            <circle
              className="graph-landing-node-ring"
              cx={node.x}
              cy={node.y}
              r={(node.size / 2) - 1.4}
              stroke={node.color}
            />
          </g>
        ))}
      </svg>
      <PreviewCard className="graph-landing-preview-card-a" label={panelA[0]} value={panelA[1]} />
      <PreviewCard className="graph-landing-preview-card-b" label={panelB[0]} value={panelB[1]} />
    </div>
  );
}

function AtlasAsset({ panelA, panelB }) {
  return (
    <div className="graph-landing-preview graph-landing-asset graph-landing-asset--atlas" aria-hidden="true">
      <svg viewBox="0 0 100 100" role="img">
        <path className="graph-landing-map-path graph-landing-map-path-main" d="M10 72 C24 48 30 50 42 38 S67 16 88 26" />
        <path className="graph-landing-map-path graph-landing-map-path-soft" d="M18 26 C34 34 37 58 54 60 S74 54 89 76" />
        <path className="graph-landing-map-path graph-landing-map-path-soft" d="M11 49 C29 54 42 78 61 71 S76 38 91 42" />
        {[
          [12, 72, '#00d5ff', 'Start'],
          [42, 38, '#ff2d95', 'Bridge'],
          [67, 20, '#32ff6a', 'Group'],
          [88, 26, '#ffd60a', 'Party'],
          [54, 60, '#8b5cf6', 'Story'],
        ].map(([x, y, color, label]) => (
          <g key={label}>
            <circle className="graph-landing-map-pin-glow" cx={x} cy={y} r="6" fill={color} />
            <circle className="graph-landing-map-pin" cx={x} cy={y} r="3.5" fill={color} />
            <text x={x + 5} y={y - 4}>{label}</text>
          </g>
        ))}
      </svg>
      <PreviewCard className="graph-landing-preview-card-a" label={panelA[0]} value={panelA[1]} />
      <PreviewCard className="graph-landing-preview-card-b" label={panelB[0]} value={panelB[1]} />
    </div>
  );
}

function WeddingAsset({ panelA, panelB }) {
  return (
    <div className="graph-landing-preview graph-landing-asset graph-landing-asset--wedding" aria-hidden="true">
      <div className="graph-landing-couple-frame">
        <img src={graphNodes[1].photo} alt="" />
        <span>&</span>
        <img src={graphNodes[2].photo} alt="" />
      </div>
      <div className="graph-landing-table-ring graph-landing-table-ring-a" />
      <div className="graph-landing-table-ring graph-landing-table-ring-b" />
      <div className="graph-landing-table-ring graph-landing-table-ring-c" />
      {Array.from({ length: 18 }, (_, index) => (
        <span key={index} className={`graph-landing-seat graph-landing-seat-${index}`} />
      ))}
      <div className="graph-landing-wedding-note">guest paths</div>
      <PreviewCard className="graph-landing-preview-card-a" label={panelA[0]} value={panelA[1]} />
      <PreviewCard className="graph-landing-preview-card-b" label={panelB[0]} value={panelB[1]} />
    </div>
  );
}

function FamilyAsset({ panelA, panelB }) {
  const familyPeople = [graphNodes[6], graphNodes[0], graphNodes[1], graphNodes[2], graphNodes[3], graphNodes[7], graphNodes[8]];
  return (
    <div className="graph-landing-preview graph-landing-asset graph-landing-asset--family" aria-hidden="true">
      <svg viewBox="0 0 100 100" role="img">
        <path d="M50 13 V28 M28 28 H72 M28 28 V45 M50 28 V45 M72 28 V45 M40 58 H62 M40 58 V74 M62 58 V74" />
      </svg>
      {familyPeople.map((person, index) => (
        <div key={person.name} className={`graph-landing-family-person graph-landing-family-person-${index}`}>
          <img src={person.photo} alt="" />
          <span>{person.name}</span>
        </div>
      ))}
      <PreviewCard className="graph-landing-preview-card-a" label={panelA[0]} value={panelA[1]} />
      <PreviewCard className="graph-landing-preview-card-b" label={panelB[0]} value={panelB[1]} />
    </div>
  );
}

function TimelineAsset({ panelA, panelB }) {
  const events = ['Kevin met Maya', 'Maya brought Alex', 'Alex met Priya', 'Avery joined', 'Marcus connected'];
  return (
    <div className="graph-landing-preview graph-landing-asset graph-landing-asset--timeline" aria-hidden="true">
      <div className="graph-landing-timeline-stage">
        {events.map((event, index) => (
          <div key={event} className="graph-landing-timeline-event">
            <span>{String(index + 1).padStart(2, '0')}</span>
            <strong>{event}</strong>
            <em>{2020 + index}</em>
          </div>
        ))}
      </div>
      <div className="graph-landing-timeline-controls">
        <span />
        <span />
        <span />
      </div>
      <PreviewCard className="graph-landing-preview-card-a" label={panelA[0]} value={panelA[1]} />
      <PreviewCard className="graph-landing-preview-card-b" label={panelB[0]} value={panelB[1]} />
    </div>
  );
}

function InsightsAsset({ panelA, panelB }) {
  const insightCards = [
    ['Shortest path', 'Kevin -> Priya -> Avery'],
    ['Hidden connector', 'Maya bridges two circles'],
    ['What if?', '3 people stay linked'],
    ['Oldest bond', '2016'],
  ];
  return (
    <div className="graph-landing-preview graph-landing-asset graph-landing-asset--insights" aria-hidden="true">
      <div className="graph-landing-insight-radar">
        <span />
        <span />
        <span />
      </div>
      <div className="graph-landing-insight-grid">
        {insightCards.map(([label, value]) => (
          <div key={label}>
            <span>{label}</span>
            <strong>{value}</strong>
          </div>
        ))}
      </div>
      <PreviewCard className="graph-landing-preview-card-a" label={panelA[0]} value={panelA[1]} />
      <PreviewCard className="graph-landing-preview-card-b" label={panelB[0]} value={panelB[1]} />
    </div>
  );
}

function MinimalAsset({ panelA, panelB }) {
  return (
    <div className="graph-landing-preview graph-landing-asset graph-landing-asset--minimal" aria-hidden="true">
      <div className="graph-landing-minimal-wordmark">
        <span>h</span><span>y</span><span>m</span><span>m</span>
      </div>
      <div className="graph-landing-minimal-lock">
        <span />
      </div>
      <div className="graph-landing-minimal-link">/map/private-name</div>
      <PreviewCard className="graph-landing-preview-card-a" label={panelA[0]} value={panelA[1]} />
      <PreviewCard className="graph-landing-preview-card-b" label={panelB[0]} value={panelB[1]} />
    </div>
  );
}

function StudioAsset({ panelA, panelB }) {
  return (
    <div className="graph-landing-preview graph-landing-asset graph-landing-asset--studio" aria-hidden="true">
      <div className="graph-landing-story-card graph-landing-story-card-a">
        <img src={graphNodes[5].photo} alt="" />
        <span>shared story</span>
        <strong>First river trip</strong>
      </div>
      <div className="graph-landing-story-card graph-landing-story-card-b">
        <img src={graphNodes[7].photo} alt="" />
        <span>tagged with</span>
        <strong>Avery, Kevin</strong>
      </div>
      <div className="graph-landing-story-card graph-landing-story-card-c">
        <img src={graphNodes[3].photo} alt="" />
        <span>memory date</span>
        <strong>June 2021</strong>
      </div>
      <PreviewCard className="graph-landing-preview-card-a" label={panelA[0]} value={panelA[1]} />
      <PreviewCard className="graph-landing-preview-card-b" label={panelB[0]} value={panelB[1]} />
    </div>
  );
}

function EditorialAsset({ panelA, panelB }) {
  return (
    <div className="graph-landing-preview graph-landing-asset graph-landing-asset--editorial" aria-hidden="true">
      <div className="graph-landing-question-card">
        <span>Question</span>
        <strong>How do you know Jordan?</strong>
      </div>
      <div className="graph-landing-answer-path">
        {['You', 'Maya', 'Priya', 'Jordan'].map((name, index) => (
          <div key={name}>
            <span>{index + 1}</span>
            <strong>{name}</strong>
          </div>
        ))}
      </div>
      <div className="graph-landing-editorial-caption">small-world shortcut included</div>
      <PreviewCard className="graph-landing-preview-card-a" label={panelA[0]} value={panelA[1]} />
      <PreviewCard className="graph-landing-preview-card-b" label={panelB[0]} value={panelB[1]} />
    </div>
  );
}

function LaunchAsset({ panelA, panelB }) {
  const cells = ['Social', 'Family', 'Wedding', 'Timeline', 'Insights', 'Stories', 'Photos', 'Suggestions', 'Share'];
  return (
    <div className="graph-landing-preview graph-landing-asset graph-landing-asset--launch" aria-hidden="true">
      <div className="graph-landing-launch-matrix">
        {cells.map((cell, index) => (
          <div key={cell} className={`graph-landing-launch-cell graph-landing-launch-cell-${index}`}>
            <span>{cell}</span>
          </div>
        ))}
      </div>
      <div className="graph-landing-launch-beam" />
      <PreviewCard className="graph-landing-preview-card-a" label={panelA[0]} value={panelA[1]} />
      <PreviewCard className="graph-landing-preview-card-b" label={panelB[0]} value={panelB[1]} />
    </div>
  );
}

function ProductCarouselAsset({ variant }) {
  const legacyAssets = {
    atlas: AtlasAsset,
    wedding: WeddingAsset,
    family: FamilyAsset,
    timeline: TimelineAsset,
    insights: InsightsAsset,
    minimal: MinimalAsset,
    studio: StudioAsset,
    editorial: EditorialAsset,
    launch: LaunchAsset,
  };
  const AccentAsset = legacyAssets[variant.key] || NetworkAsset;
  const variantFeatures = variant.featureCards || featureCards;
  const slides = [
    {
      label: variant.panelA?.[0] || 'Map view',
      title: variant.panelA?.[1] || variant.title,
      body: variant.body,
      photo: productPhotos[0],
    },
    {
      label: variantFeatures[1]?.title || 'Timeline',
      title: variantFeatures[1]?.title || 'Watch the group form',
      body: variantFeatures[1]?.body || 'Replay how people joined the group without losing the current map layout.',
      photo: productPhotos[1],
    },
    {
      label: variantFeatures[2]?.title || 'Insights',
      title: variantFeatures[2]?.title || 'Reveal the hidden connectors',
      body: variantFeatures[2]?.body || 'Turn shortest paths, central people, and small-world links into visual answers on the map.',
      photo: productPhotos[2],
    },
  ];
  const [activeSlideIndex, setActiveSlideIndex] = useState(0);
  const activeSlide = slides[activeSlideIndex];
  const showPreviousSlide = () => setActiveSlideIndex((index) => (index + slides.length - 1) % slides.length);
  const showNextSlide = () => setActiveSlideIndex((index) => (index + 1) % slides.length);

  return (
    <div className="graph-landing-preview graph-landing-asset graph-product-carousel">
      <div className="graph-product-carousel-photo">
        <img src={activeSlide.photo} alt="" />
        <div className="graph-product-carousel-network" aria-hidden="true">
          {graphNodes.slice(0, 6).map((node, index) => (
            <span key={node.name} className={`graph-product-carousel-node graph-product-carousel-node-${index}`}>
              <img src={node.photo} alt="" />
            </span>
          ))}
        </div>
      </div>
      <div className="graph-product-carousel-mini" aria-hidden="true">
        <AccentAsset panelA={variant.panelA} panelB={variant.panelB} />
      </div>
      <div className="graph-product-carousel-copy">
        <span>{activeSlide.label}</span>
        <strong>{activeSlide.title}</strong>
        <p>{activeSlide.body}</p>
      </div>
      <div className="graph-product-carousel-controls" aria-label="Product preview controls">
        <button type="button" onClick={showPreviousSlide} aria-label="Previous preview">{'<'}</button>
        <div>
          {slides.map((slide, index) => (
            <button
              key={slide.label}
              type="button"
              className={index === activeSlideIndex ? 'graph-product-carousel-dot graph-product-carousel-dot--active' : 'graph-product-carousel-dot'}
              onClick={() => setActiveSlideIndex(index)}
              aria-label={`Show ${slide.label}`}
            />
          ))}
        </div>
        <button type="button" onClick={showNextSlide} aria-label="Next preview">{'>'}</button>
      </div>
    </div>
  );
}

function WeddingSalesAsset({ variant }) {
  const slides = [
    {
      label: 'Bride',
      title: variant.featureCards?.[0]?.title || 'Guest connection map',
      body: variant.featureCards?.[0]?.body || variant.body,
      photo: weddingPeople.bride.photo,
    },
    {
      label: 'Groom',
      title: variant.featureCards?.[1]?.title || 'Wedding weekend guide',
      body: variant.featureCards?.[1]?.body || variant.body,
      photo: weddingPeople.groom.photo,
    },
    {
      label: 'Wedding Party',
      title: variant.featureCards?.[2]?.title || 'Conversation starters',
      body: variant.featureCards?.[2]?.body || variant.body,
      photo: weddingPeople.weddingParty[1],
    },
  ];
  const [activeSlideIndex, setActiveSlideIndex] = useState(0);
  const activeSlide = slides[activeSlideIndex];
  const showPreviousSlide = () => setActiveSlideIndex((index) => (index + slides.length - 1) % slides.length);
  const showNextSlide = () => setActiveSlideIndex((index) => (index + 1) % slides.length);

  return (
    <div className={`graph-landing-preview graph-landing-asset graph-landing-wedding-sales graph-landing-wedding-sales--${variant.asset}`}>
      <div className="graph-wedding-carousel" aria-label={`${variant.title} preview carousel`}>
        <div className="graph-wedding-carousel-photo">
          <img src={activeSlide.photo} alt="" />
        </div>
        <div className="graph-wedding-carousel-copy">
          <span>{activeSlide.label}</span>
          <strong>{activeSlide.title}</strong>
          <p>{activeSlide.body}</p>
        </div>
        <div className="graph-wedding-carousel-controls" aria-label="Wedding preview controls">
          <button type="button" onClick={showPreviousSlide} aria-label="Previous wedding preview">{'<'}</button>
          <div>
            {slides.map((slide, index) => (
              <button
                key={slide.label}
                type="button"
                className={index === activeSlideIndex ? 'graph-wedding-carousel-dot graph-wedding-carousel-dot--active' : 'graph-wedding-carousel-dot'}
                onClick={() => setActiveSlideIndex(index)}
                aria-label={`Show ${slide.label}`}
              />
            ))}
          </div>
          <button type="button" onClick={showNextSlide} aria-label="Next wedding preview">{'>'}</button>
        </div>
        <div className="graph-wedding-carousel-meta">
          <div>
            <span>Bride</span>
            <img src={weddingPeople.bride.photo} alt="" />
          </div>
          <div>
            <span>Groom</span>
            <img src={weddingPeople.groom.photo} alt="" />
          </div>
          <div>
            <span>Party</span>
            <img src={weddingPeople.weddingParty[0]} alt="" />
          </div>
        </div>
      </div>
    </div>
  );
}

function WeddingLandingDetails({ variant }) {
  const productMoments = [
    {
      eyebrow: 'Guest view',
      title: 'Help guests feel like they already belong.',
      body: 'Before cocktail hour starts, guests can see familiar faces, who they are one introduction away from, and a few warm ways into the room.',
      photo: weddingPeople.couple,
    },
    {
      eyebrow: 'Reception view',
      title: 'Make every table feel thoughtfully connected.',
      body: 'Show the little threads that make a table work: shared stories, mutual friends, family ties, and the person who can make the first hello easy.',
      photo: weddingPeople.weddingParty[2],
    },
    {
      eyebrow: 'Memory view',
      title: 'Turn the weekend into a living guest book.',
      body: 'After the last song, photos and stories stay attached to the people who made them, so the wedding keeps feeling alive after everyone goes home.',
      photo: weddingPeople.guests[2],
    },
  ];
  const bridalDetails = [
    {
      title: 'For brides',
      body: 'A beautiful private link that makes the wedding feel more personal before guests arrive.',
      exampleLabel: 'Guest welcome',
      exampleTitle: 'A private map guests can open early.',
      exampleText: 'Guests can browse the couple, family sides, wedding party, stories, and relationship paths.',
    },
    {
      title: 'For planners',
      body: 'A polished relationship layer for couples who want the guest experience to feel more personal.',
      exampleLabel: 'Editor tools',
      exampleTitle: 'Guest context in one place.',
      exampleText: 'Organize people, relationship categories, stories, photos, and guest suggestions.',
    },
    {
      title: 'For guests',
      body: 'Less awkward small talk, more warm introductions, and an easy way to remember everyone after the weekend.',
      exampleLabel: 'Prototype prompt',
      exampleTitle: 'Ask about the Tahoe trip.',
      exampleText: 'A future prompt layer could turn shared stories into easy conversation starters.',
    },
  ];

  return (
    <div className="graph-wedding-details">
      <section className="graph-wedding-editorial" aria-label="How You Met Us product story">
        <div>
          <span>How You Met Us</span>
          <h2>Built for the moments brides actually care about.</h2>
        </div>
        <p>
          {variant.body} The experience should feel like part of the wedding website: elegant, private,
          photo-forward, and easy for guests to understand on their phones.
        </p>
      </section>

      <section className="graph-wedding-detail-cards" aria-label="Wedding customer benefits">
        {bridalDetails.map((detail) => (
          <article key={detail.title}>
            <div>
              <span>{detail.title}</span>
              <p>{detail.body}</p>
            </div>
            <div className="graph-wedding-detail-example">
              <span>{detail.exampleLabel}</span>
              <strong>{detail.exampleTitle}</strong>
              <p>{detail.exampleText}</p>
            </div>
          </article>
        ))}
      </section>

      <section className="graph-wedding-product-grid" aria-label="Wedding product examples">
        {productMoments.map((moment) => (
          <article key={moment.title} className="graph-wedding-product-card">
            <img src={moment.photo} alt="" />
            <div>
              <span>{moment.eyebrow}</span>
              <h3>{moment.title}</h3>
              <p>{moment.body}</p>
            </div>
          </article>
        ))}
      </section>

      <section className="graph-wedding-app-samples" aria-label="Wedding app sample screens">
        <div className="graph-wedding-app-samples-copy">
          <span>Prototype samples</span>
          <h2>A private guide to everyone in the room.</h2>
          <p>
            Preview the wedding layer: guest cards, relationship paths, shared stories, and memories
            that help the weekend feel warmer before guests ever sit down.
          </p>
        </div>
        <div className="graph-wedding-phone-row">
          <article className="graph-wedding-phone">
            <div className="graph-wedding-phone-top">
              <span>Welcome</span>
              <strong>Emma & Daniel</strong>
            </div>
            <div className="graph-wedding-phone-couple">
              <img src={weddingPeople.bride.photo} alt="" />
              <i />
              <img src={weddingPeople.groom.photo} alt="" />
            </div>
            <div className="graph-wedding-phone-card">
              <span>Tonight</span>
              <strong>Browse the people behind the party</strong>
              <p>College friends, cousins, coworkers, and the friend who introduced the couple.</p>
            </div>
          </article>
          <article className="graph-wedding-phone">
            <div className="graph-wedding-phone-top">
              <span>Path finder</span>
              <strong>How you know Mia</strong>
            </div>
            <div className="graph-wedding-path-sample">
              {['You', 'Bride', 'Roommate', 'Mia'].map((name) => (
                <div key={name}>
                  <span>{name}</span>
                </div>
              ))}
            </div>
            <div className="graph-wedding-phone-card">
              <span>Icebreaker</span>
              <strong>Ask about the Tahoe trip</strong>
              <p>A shared story makes the first hello feel easy.</p>
            </div>
          </article>
          <article className="graph-wedding-phone">
            <div className="graph-wedding-phone-top">
              <span>Memory book</span>
              <strong>Shared after the last song</strong>
            </div>
            <img className="graph-wedding-phone-photo" src={weddingPeople.weddingParty[2]} alt="" />
            <div className="graph-wedding-phone-card">
              <span>Memory added</span>
              <strong>Photo with the wedding party</strong>
              <p>Tagged with bride, groom, and college friends.</p>
            </div>
          </article>
        </div>
      </section>

      <section className="graph-wedding-full-photo" aria-label="Wedding party preview">
        <img src={weddingPeople.weddingParty[1]} alt="" />
        <div>
          <span>Wedding party ready</span>
          <h2>Give your closest people the context to host the room.</h2>
          <p>
            The live map already supports guest paths, family sides, relationship categories, stories,
            photos, and memory collection. The prototype layer shows how that can become an even more guided
            wedding-weekend experience.
          </p>
        </div>
      </section>
    </div>
  );
}

function ProductLandingDetails({ variant }) {
  const variantFeatures = variant.featureCards || featureCards;
  const productMoments = [
    {
      eyebrow: 'Relationship map',
      title: 'A visual homepage for the people in the group.',
      body: 'Photos, connection categories, stories, and original introductions all live in one map that feels easy to browse.',
      photo: productPhotos[3],
    },
    {
      eyebrow: 'Animated history',
      title: 'Replay how the community actually formed.',
      body: 'The timeline keeps the layout stable while edges appear over time, so the map becomes a story people can watch.',
      photo: productPhotos[4],
    },
    {
      eyebrow: 'Insight mode',
      title: 'Turn social questions into clear visual answers.',
      body: 'Shortest paths, quiet bridges, central connectors, and what-if simulations animate directly on the map.',
      photo: productPhotos[5],
    },
  ];

  return (
    <div className="graph-product-details">
      <section className="graph-product-editorial" aria-label="Product overview">
        <div>
          <span>{variant.featureIntro}</span>
          <h2>{variant.featureTitle}</h2>
        </div>
        <p>
          {variant.body} The landing experience now gives people a larger product story before they open a private map.
        </p>
      </section>

      <section className="graph-product-story-carousel" aria-label="Map product examples">
        {productMoments.map((moment, index) => (
          <article key={moment.title} className="graph-product-story-card">
            <img src={moment.photo} alt="" />
            <div>
              <span>{moment.eyebrow}</span>
              <h3>{moment.title}</h3>
              <p>{moment.body}</p>
            </div>
            <small>{String(index + 1).padStart(2, '0')}</small>
          </article>
        ))}
      </section>

      <section className="graph-product-app-samples" aria-label="Map app sample screens">
        <div className="graph-product-app-samples-copy">
          <span>App samples</span>
          <h2>Preview the map screens people can use today.</h2>
          <p>
            The landing page shows the live product story: map browsing, person cards, connection
            context, and insight flows from the current app experience.
          </p>
        </div>
        <div className="graph-product-sample-grid">
          <article>
            <span>Person card</span>
            <img src={graphNodes[6].photo} alt="" />
            <strong>Kevin Vissuet</strong>
            <p>Connections, stories, photos, and suggested edits in one clean profile.</p>
          </article>
          <article>
            <span>Connection view</span>
            <div className="graph-product-sample-path">
              {graphNodes.slice(0, 4).map((node) => (
                <img key={node.name} src={node.photo} alt="" />
              ))}
            </div>
            <strong>How they met</strong>
            <p>Relationship category, date, origin story, and related memories stay together.</p>
          </article>
          <article>
            <span>Insight view</span>
            <div className="graph-product-sample-award">5</div>
            <strong>Degrees of separation</strong>
            <p>Shortest paths and hidden connectors animate directly on the map.</p>
          </article>
        </div>
      </section>

      <section className="graph-product-feature-wall" aria-label="Feature highlights">
        {variantFeatures.map((feature) => (
          <article key={feature.title}>
            <span>{feature.title}</span>
            <p>{feature.body}</p>
          </article>
        ))}
      </section>
    </div>
  );
}

function LandingAsset({ variant }) {
  if (variant.key.startsWith('wedding-') && variant.asset) return <WeddingSalesAsset variant={variant} />;
  return <ProductCarouselAsset variant={variant} />;
}

function DemoContactSection({ isWeddingVariant }) {
  const mailtoHref = getDemoMailtoHref(isWeddingVariant);

  return (
    <section className={isWeddingVariant ? 'graph-landing-contact graph-landing-contact--wedding' : 'graph-landing-contact'} aria-label="Contact for demo">
      <div>
        <span>{isWeddingVariant ? 'Private demo' : 'See it live'}</span>
        <h2>{isWeddingVariant ? 'Want to see this for your wedding?' : 'Want a demo with your own map?'}</h2>
      </div>
      <p>
        {isWeddingVariant
          ? 'Email us and we can walk through what a private wedding map could look like for your guest list, stories, and event weekend.'
          : 'Email us and we can show how a private social map, family tree, or wedding map works with real people, stories, and insights.'}
      </p>
      <a href={mailtoHref}>
        Email us for a demo
        <ArrowForwardIcon fontSize="small" />
      </a>
    </section>
  );
}

export default function GraphLanding({ variantId = '1' }) {
  const [graphName, setGraphName] = useState('');
  const [error, setError] = useState('');
  const [isOpeningGraph, setIsOpeningGraph] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSignupNoticeOpen, setIsSignupNoticeOpen] = useState(false);
  const navigate = useNavigate();
  const apolloClient = useApolloClient();
  const trimmedGraphName = useMemo(() => graphName.trim(), [graphName]);
  const variant = landingVariants[variantId] || landingVariants[1];
  const isWeddingVariant = variant.key.startsWith('wedding-');
  const featureList = variant.metrics || landingVariants[1].metrics;
  const cardsForVariant = variant.featureCards || featureCards;
  const typeCardsForVariant = variant.typeCards || graphTypeCards;

  useEffect(() => {
    const previousTitle = document.title;
    document.title = isWeddingVariant ? 'How You Met Us' : variantId === '1' ? 'How you met me' : `How you met me | Landing ${variantId}`;
    return () => {
      document.title = previousTitle;
    };
  }, [isWeddingVariant, variantId]);

  const openGraph = async (event) => {
    event.preventDefault();
    if (!trimmedGraphName) {
      setError('Enter the memory map name or URL name to continue.');
      return;
    }

    setError('');
    setIsOpeningGraph(true);
    try {
      const result = await apolloClient.query({
        query: GRAPH_EXISTS_QUERY,
        variables: { graphName: trimmedGraphName },
        fetchPolicy: 'network-only',
      });

      if (!result.data?.graph?._id) {
        setError('No memory map exists for that URL name.');
        return;
      }

      navigate(getReadGraphPath(trimmedGraphName));
    } catch (loadError) {
      const message = loadError.message || '';
      setError(message.includes('Graph not found')
        ? 'No memory map exists for that URL name.'
        : 'Could not check that memory map. Try again.');
    } finally {
      setIsOpeningGraph(false);
    }
  };
  const demoMailtoHref = getDemoMailtoHref(isWeddingVariant);

  const openEditorLogin = () => {
    setIsMobileMenuOpen(false);
    navigate('/edit');
  };

  const openSignupNotice = () => {
    setIsMobileMenuOpen(false);
    setIsSignupNoticeOpen(true);
  };

  return (
    <main className={`graph-landing graph-landing--${variant.key}`}>
      <header className="graph-landing-nav">
        <div className="graph-landing-brand">
          <span className={isWeddingVariant ? 'graph-landing-mark graph-landing-mark--wedding' : 'graph-landing-mark'} aria-label={isWeddingVariant ? 'hymu' : 'hymm'}>
            <span>h</span>
            <span>y</span>
            <span>m</span>
            <span>{isWeddingVariant ? 'u' : 'm'}</span>
          </span>
          <span>{isWeddingVariant ? 'How You Met Us' : 'How You Met Me'}</span>
        </div>
        <div className="graph-landing-mobile-menu">
          <button
            type="button"
            className="graph-landing-menu-button"
            onClick={() => setIsMobileMenuOpen((open) => !open)}
            aria-expanded={isMobileMenuOpen}
            aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
          >
            {isMobileMenuOpen ? <CloseIcon fontSize="small" /> : <MenuIcon fontSize="small" />}
          </button>
          {isMobileMenuOpen && (
            <nav className="graph-landing-menu-panel" aria-label="Mobile navigation">
              <button type="button" onClick={openEditorLogin}>Log in</button>
              <button type="button" onClick={openSignupNotice}>Sign up</button>
              <a href={demoMailtoHref} onClick={() => setIsMobileMenuOpen(false)}>Contact</a>
            </nav>
          )}
        </div>
      </header>

      {isSignupNoticeOpen && (
        <div className="graph-signup-dialog-backdrop" role="presentation" onClick={() => setIsSignupNoticeOpen(false)}>
          <section
            className={isWeddingVariant ? 'graph-signup-dialog graph-signup-dialog--wedding' : 'graph-signup-dialog'}
            role="dialog"
            aria-modal="true"
            aria-labelledby="graph-signup-dialog-title"
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              className="graph-signup-dialog-close"
              onClick={() => setIsSignupNoticeOpen(false)}
              aria-label="Close sign up notice"
            >
              <CloseIcon fontSize="small" />
            </button>
            <span>{isWeddingVariant ? 'White-glove capacity' : 'Sign ups paused'}</span>
            <h2 id="graph-signup-dialog-title">Sign ups are closed for now.</h2>
            <p>
              We are sorry, but we have more customers than we can currently give our best work to.
              Every wedding gets a white-glove treatment, because these memories deserve to feel
              cared for and last forever.
            </p>
            <a href={demoMailtoHref}>Email us for a demo</a>
          </section>
        </div>
      )}

      <section className="graph-landing-hero">
        <div className="graph-landing-copy">
          <span className="graph-landing-eyebrow">{variant.eyebrow}</span>
          <h1>{variant.title}</h1>
          <p>
            {variant.body}
          </p>

          <form className="graph-landing-search" onSubmit={openGraph}>
            <label htmlFor={`graph-name-${variantId}`}>{variant.label}</label>
            <div className="graph-landing-search-row">
              <input
                id={`graph-name-${variantId}`}
                value={graphName}
                onChange={(event) => {
                  setGraphName(event.target.value);
                  setError('');
                }}
                placeholder={variant.placeholder}
                autoComplete="off"
              />
              <button type="submit" aria-label="Open map" disabled={isOpeningGraph}>
                <span>{isOpeningGraph ? 'Checking...' : variant.button}</span>
                <ArrowForwardIcon fontSize="small" />
              </button>
            </div>
            {error && <small>{error}</small>}
          </form>

          <div className="graph-landing-metrics" aria-label="Landing highlights">
            {featureList.map((metric) => (
              <span key={metric}>{metric}</span>
            ))}
          </div>
        </div>

        <LandingAsset variant={variant} />
      </section>

      <section className="graph-landing-features" aria-label="Map tools">
        {cardsForVariant.map((feature) => {
          const Icon = feature.icon;
          return (
            <article key={feature.title} className="graph-landing-feature">
              <Icon fontSize="small" />
              <h2>{feature.title}</h2>
              <p>{feature.body}</p>
            </article>
          );
        })}
      </section>

      <section className="graph-landing-types" aria-label="Map styles">
        <div className="graph-landing-section-heading">
          <span>{variant.featureIntro}</span>
          <h2>{variant.featureTitle}</h2>
        </div>
        <div className="graph-landing-type-grid">
          {typeCardsForVariant.map((type) => (
            <article key={type.title} className="graph-landing-type-card">
              <h3>{type.title}</h3>
              <p>{type.body}</p>
            </article>
          ))}
        </div>
      </section>

      {isWeddingVariant && <WeddingLandingDetails variant={variant} />}
      {!isWeddingVariant && <ProductLandingDetails variant={variant} />}
      <DemoContactSection isWeddingVariant={isWeddingVariant} />
    </main>
  );
}
